import React, { useState } from "react";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { postEnquiry } from "../lib/api";

const inputCls =
  "w-full border border-ivory/20 bg-white/5 px-4 py-3 text-sm text-ivory placeholder:text-ivory/40 focus:border-ivory focus:outline-none transition-colors duration-300";

const labelCls = "eyebrow mb-2 block text-ivory/60";

export const EnquiryForm = ({ kind = "general", listingId, listingTitle, compact = false }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 2) er.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Please enter a valid email address.";
    if (form.phone.replace(/\D/g, "").length < 8) er.phone = "Please enter a contact number so Andy can reach you.";
    if (form.message.trim().length < 10) er.message = "Please write at least 10 characters so Andy's team can help.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      const res = await postEnquiry({ ...form, kind, listing_id: listingId, listing_title: listingTitle });
      setConfirmation(res.message);
      toast.success("Enquiry recorded in the preview database.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit. Please check your details and try again.");
    } finally {
      setSending(false);
    }
  };

  if (confirmation) {
    return (
      <div className="border border-ivory/25 bg-white/5 p-8" data-testid="enquiry-success">
        <CheckCircle2 className="h-8 w-8 text-ivory" />
        <h3 className="mt-4 font-serif text-2xl text-ivory">Thank you, {form.name.split(" ")[0]}.</h3>
        <p className="mt-3 text-sm leading-relaxed text-ivory/70">{confirmation}</p>
        <p className="mt-4 text-xs text-ivory/50">
          Prefer to talk now? Call Andy at <a className="underline" href="tel:+6592272638">+65 9227 2638</a>.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate data-testid="enquiry-form" className={compact ? "space-y-4" : "space-y-5"}>
      <div className={compact ? "space-y-4" : "grid gap-5 sm:grid-cols-2"}>
        <div>
          <label htmlFor={`enquiry-name-${kind}`} className={labelCls}>Name</label>
          <input
            id={`enquiry-name-${kind}`}
            data-testid="enquiry-name-input"
            className={inputCls}
            placeholder="Your full name"
            value={form.name}
            onChange={set("name")}
          />
          {errors.name && <p className="mt-2 border-l-2 border-brand pl-2 text-xs text-ivory" data-testid="enquiry-name-error">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor={`enquiry-email-${kind}`} className={labelCls}>Email</label>
          <input
            id={`enquiry-email-${kind}`}
            type="email"
            data-testid="enquiry-email-input"
            className={inputCls}
            placeholder="you@example.com"
            value={form.email}
            onChange={set("email")}
          />
          {errors.email && <p className="mt-2 border-l-2 border-brand pl-2 text-xs text-ivory" data-testid="enquiry-email-error">{errors.email}</p>}
        </div>
      </div>

      <div>
        <label htmlFor={`enquiry-phone-${kind}`} className={labelCls}>Mobile number</label>
        <input
          id={`enquiry-phone-${kind}`}
          data-testid="enquiry-phone-input"
          className={inputCls}
          placeholder="+65 9xxx xxxx"
          value={form.phone}
          onChange={set("phone")}
        />
        {errors.phone && <p className="mt-2 border-l-2 border-brand pl-2 text-xs text-ivory" data-testid="enquiry-phone-error">{errors.phone}</p>}
      </div>

      <div>
        <label htmlFor={`enquiry-message-${kind}`} className={labelCls}>Message</label>
        <textarea
          id={`enquiry-message-${kind}`}
          rows={compact ? 3 : 4}
          data-testid="enquiry-message-input"
          className={`${inputCls} resize-none`}
          placeholder={kind === "valuation" ? "Property address, type and land size…" : "Tell us about your landed property goals…"}
          value={form.message}
          onChange={set("message")}
        />
        {errors.message && <p className="mt-2 border-l-2 border-brand pl-2 text-xs text-ivory" data-testid="enquiry-message-error">{errors.message}</p>}
      </div>

      <button
        type="submit"
        disabled={sending}
        data-testid="enquiry-submit-button"
        className="group flex w-full items-center justify-center gap-3 bg-brand px-8 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-ivory transition-colors duration-300 hover:bg-brand-dark disabled:opacity-60"
      >
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        {kind === "valuation" ? "Request a Valuation" : "Send Enquiry"}
        {!sending && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
      </button>
      <p className="text-[0.7rem] leading-relaxed text-ivory/40">
        Your enquiry goes straight to Andy's inbox and is saved in the admin dashboard. Not financial advice.
      </p>
    </form>
  );
};
